import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { useCourse } from "../context/CourseContext";
import { apiFetch } from "../utils/apiFetch";
import { ownerPlan } from "../utils/communityEntitlements.mjs";
import { FiLoader } from "react-icons/fi";
import GroupPricingFields from "./GroupPricingFields";

export default function CommunityPricingSettings({ onSaved }) {
  const { course, setCourse } = useCourse();
  const { userId, userInfo } = useAuth();
  const client = useQueryClient();
  const configured = course.pricing?.access === "paid";
  const [pricing, setPricing] = useState({ access: "paid", amount: configured ? String(course.pricing.amount / 100) : "", currency: "INR" });
  const [bankReady, setBankReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const eligible = course.store?.enabled ?? ownerPlan(userInfo?.subscriptionInfo) !== "default";
  const save = async () => {
    if (saving || !eligible || !bankReady) return;
    if (!/^\d{1,7}(\.\d{1,2})?$/.test(pricing.amount) || Number(pricing.amount) < 1) {
      setError("Enter a price of at least INR 1.00 with at most two decimal places.");
      return;
    }
    setSaving(true); setError(""); setSaved(false);
    try {
      const response = await apiFetch(`${process.env.API_URL}/api/groups/${course._id}/pricing`, {
        method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ pricing }),
      });
      const data = await response.json();
      if (!response.ok) throw Error(data.message || "Could not save premium membership.");
      setCourse(previous => previous?._id === course._id ? { ...previous, pricing: data.pricing } : previous);
      await client.invalidateQueries({ queryKey: ["account", userId] });
      setSaved(true); onSaved?.();
    } catch (error) { setError(error.message); }
    finally { setSaving(false); }
  };
  return <div className="max-w-xl space-y-5">
    <h1 className="text-xl font-semibold">Premium membership</h1>
    <GroupPricingFields value={pricing} onChange={setPricing} disabled={saving} onBankReady={setBankReady} eligible={eligible} paidOnly />
    {configured && <p className="text-sm text-[var(--app-secondary)]">Price changes apply to new subscriptions. Existing subscriptions keep their current monthly fee.</p>}
    {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
    {saved && <p role="status" className="text-sm text-green-700">Monthly premium fee saved.</p>}
    <button type="button" onClick={save} disabled={saving || !eligible || !bankReady} className="inline-flex min-h-11 items-center gap-2 rounded-md bg-orange-700 px-4 text-sm font-semibold text-white disabled:opacity-50">{saving && <FiLoader className="animate-spin" />}{configured ? "Save monthly fee" : "Enable premium membership"}</button>
  </div>;
}
